import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  Stack,
  Typography,
  Paper,
  Skeleton
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import RefinedTickets from "./refined-tickets-input";
import { TicketGenerationRequestBody } from "@/types";
import { useTicketContext } from "@/views/pages/tickets/TicketContext";
import {
  useGetTicketTypesQuery,
  usePostTicketPayloadToGitLabMutation
} from "@/services/ticket-service/ticketGeneratorApis";
import { GenerateTicketService } from "@/services/ticket-service/gernerate-ticket";
import { toast } from "react-toastify";
import TicketCreatedSuccessComponent from "./ticket-success";

/**
 * This component is responsible for generating the ticket content based on the user input in the ticket configuration.
 * It handles the AI-generated content, editing, saving, and submitting the ticket to GitLab.
 * It also displays the ticket history and allows the user to revert to a previous version.
 */
const GeneratedTickets = () => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardheight, setCardHeight] = useState(0);
  const [ticketResposeSuccess, setTicketResponseSuccess] = useState(false);
  const [createdTicketUrl, setCreatedTicketUrl] = useState<string>("");
  const [originalContent, setOriginalContent] = useState<{ title: string; description: string }>({
    title: "",
    description: ""
  }); // Store the original content when editing to prevent data loss when reverting

  const { data: ticketType } = useGetTicketTypesQuery(); // Fetch ticket types
  /**
   * Get the ticket context and history from the ticket context
   */
  const {
    generatedTicketPayload,
    content,
    setContent,
    history,
    setHistory,
    isLoading,
    setIsLoading,
    ticketType: selectedTicketType
  } = useTicketContext();
  // Get the postTicketPayloadToGitLab mutation
  const [postTicketPayloadToGitLab, { isLoading: ticketPostLoading }] =
    usePostTicketPayloadToGitLabMutation();

  const handleCopy = () => {
    /**
     * Copy the content to the clipboard
     */
    navigator.clipboard.writeText(`${content.title}\n\n${content.description}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    /**
     * Save the content
     */
    setIsEditing(false);
    // Save the content to history
    setHistory((prev) => [
      ...prev,
      { context: "Some context here", title: content.title, description: content.description }
    ]);
  };

  /**
   * Handle the refined message from the user and generate a new ticket payload based on the history
   * @param {string} message The refined message from the user
   */
  const handleTicketRefine = async (message: string) => {
    setIsRefining(true);
    setIsLoading(true);

    const ticketTypeName = ticketType?.find(
      (item) => item.id === generatedTicketPayload?.ticketType
    )?.systemName;

    const payload: TicketGenerationRequestBody = {
      // ticketType: ticketTypeName?.toLocaleLowerCase() as TicketGenerationRequestBody["ticketType"] || "",
      ticketType: selectedTicketType as string,
      context: message,
      history: history || [] // Ensure history is always an array
    };
    console.log("payload", payload);
    try {
      const response = await GenerateTicketService(payload);
      if (!response) {
        throw new Error("Error generating ticket");
      }
      setContent(response);
      setHistory((prev) => [
        ...prev,
        { context: message, title: response.title, description: response.description }
      ]);
    } catch (error: any) {
      toast.error(error?.message || "Error generating ticket");
    } finally {
      setIsRefining(false);
      setIsLoading(false);
    }
  };

  const handleCreateTicketToGitLab = async () => {
    /**
     * this function to submit the ticket to gitlab
     */
    const payload = {
      gitlabProjectId: Number(generatedTicketPayload?.gitlabRepository),
      title: content.title,
      description: content.description,
      parentIssueId: Number(generatedTicketPayload?.linkedUserStory),
      ticketTypeId: generatedTicketPayload?.ticketType
    };
    try {
      const res = await postTicketPayloadToGitLab(payload).unwrap();
      if (!res) throw new Error("Something went wrong");
      if (res) {
        setTicketResponseSuccess(true);
        setCreatedTicketUrl(res?.webUrl as string);
      }
    } catch (error: any) {
      console.log(error);
      toast.error(error?.message || "Something went wrong");
    }
  };

  const calculateHeight = () => {
    /**
     * Calculate the height of the card dynamically for the history menu
     */
    if (cardRef.current) {
      const height = cardRef.current?.getBoundingClientRect().height || 0;
      setCardHeight(height);
    }
  };

  useEffect(() => {
    calculateHeight(); // Calculate height initially
    window.addEventListener("resize", calculateHeight); // Recalculate on resize

    return () => {
      window.removeEventListener("resize", calculateHeight); // Cleanup on unmount
    };
  }, []);

  return ticketResposeSuccess && createdTicketUrl ? (
    <TicketCreatedSuccessComponent
      setTicketResponseSuccess={setTicketResponseSuccess}
      setCreatedTicketUrl={setCreatedTicketUrl}
      createdTicketUrl={createdTicketUrl}
    />
  ) : (
    <Box
      position={"relative"}
      ref={cardRef}
      sx={{
        height: "fit-content",
        overflow: "auto"
      }}
    >
      <Card elevation={3} sx={{ borderRadius: 2, width: "100%" }}>
        <CardHeader
          sx={{
            mb: -1
          }}
          title="Generated Content"
          subheader="AI-generated ticket content that you can edit and refine"
          titleTypographyProps={{
            variant: "h6",
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
          subheaderTypographyProps={{
            variant: "body1",
            color: "text.secondary"
          }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {isEditing ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark
                      }
                    }}
                  >
                    Save
                  </Button>
                  <IconButton
                    onClick={() => {
                      setContent(originalContent);
                      setIsEditing(false);
                    }}
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      "&:hover": {
                        backgroundColor: theme.palette.grey[300]
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setOriginalContent(content);
                      setIsEditing(true);
                    }}
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                        color: "white",
                        borderColor: theme.palette.primary.dark
                      }
                    }}
                    disabled={!content.description || !content.title}
                  >
                    Edit
                  </Button>
                  <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                    <IconButton
                      onClick={handleCopy}
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                        "&:hover": {
                          backgroundColor: theme.palette.grey[200]
                        }
                      }}
                    >
                      {copied ? <DoneIcon color="error" /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              <Tooltip title="View history">
                <IconButton onClick={() => setIsHistoryOpen(true)} sx={{ ml: 1 }}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, mt: -1 }}>
          {/* Ticket Content Section */}
          {isLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="rectangular" height={80} />
            </Box>
          ) : (
            <Box>
              {isEditing ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Ticket Title"
                    value={content.title}
                    onChange={(e) => setContent({ ...content, title: e.target.value })}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Description"
                    value={content.description}
                    onChange={(e) => setContent({ ...content, description: e.target.value })}
                    multiline
                    minRows={5}
                    fullWidth
                    variant="outlined"
                    helperText="Use markdown for formatting (headings, lists, etc.)"
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Ticket Title"
                    variant="outlined"
                    value={content.title}
                    InputProps={{
                      readOnly: true,
                      sx: { fontWeight: 600, color: theme.palette.text.primary }
                    }}
                  />
                  <TextField
                    label="Ticket Description"
                    multiline
                    minRows={8}
                    maxRows={8}
                    value={content.description}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: "monospace", fontSize: "1.25rem" }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Action Column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
            <RefinedTickets onRefineTicketSubmit={handleTicketRefine} isLoading={isRefining} />
            <Divider />

            {/* Create Ticket Button */}
            <Button
              variant="contained"
              fullWidth
              sx={{
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                fontWeight: 600,
                backgroundColor: theme.palette.error.main
              }}
              onClick={handleCreateTicketToGitLab}
              disabled={isEditing || ticketPostLoading || !content.title || !content.description}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="6" x2="6" y1="3" y2="15"></line>
                <circle cx="18" cy="6" r="3"></circle>
                <circle cx="6" cy="18" r="3"></circle>
                <path d="M18 9a9 9 0 0 1-9 9"></path>
              </svg>
              {!ticketPostLoading ? "Create Ticket To GitLab" : "Ticket Posting to GitLab..."}
            </Button>
          </Box>
        </CardContent>

        {/* In-card History Drawer */}
        {isHistoryOpen && (
          <Paper
            elevation={10}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              height: cardheight,
              width: "360px",
              zIndex: 10,
              borderRadius: "0 8px 8px 0",
              overflow: "auto",
              scrollbarColor: theme.palette.divider + " " + theme.palette.background.default,
              transition: "transform 0.3s ease",
              transform: "translateX(0)",
              boxShadow: theme.shadows[10]
            }}
          >
            <Box
              sx={{
                height: "fit-content",
                display: "flex",
                flexDirection: "column",
                bgcolor: theme.palette.background.paper,
                overflow: "auto"
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                bgcolor={theme.palette.primary.main}
              >
                <Typography variant="h6" color="white">
                  Ticket History
                </Typography>
                <IconButton
                  onClick={() => setIsHistoryOpen(false)}
                  sx={{ color: theme.palette.primary.contrastText }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
                {history?.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No ticket history yet
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {history?.map((item, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover
                          }
                        }}
                        onClick={() => {
                          setContent({ title: item?.title, description: item?.description });
                          setIsHistoryOpen(false);
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2
                          }}
                        >
                          <Typography fontWeight={600} gutterBottom>
                            {item?.title}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            sx={{ mr: 1 }}
                            onClick={() => {
                              setContent({ title: item?.title, description: item?.description });
                              setIsHistoryOpen(false);
                            }}
                          >
                            Revert
                          </Button>
                        </Box>

                        <Typography variant="body1" color="text.secondary" whiteSpace="pre-line">
                          {item?.description?.slice(0, 120)}...
                        </Typography>
                        <Typography variant="body2" color="text.disabled" mt={1} display="block">
                          Context: {item?.context}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Card>
    </Box>
  );
};

export default GeneratedTickets;
